import {
  useRef,
  useState,
  useEffect
} from 'react';
import { format } from 'date-fns';
import * as yup from 'yup';
import db from '@services/firebase-service';
import { yupValidator } from '@lib/yup-validator';
import {
  query,
  addDoc,
  orderBy,
  onSnapshot,
  collection,
  limitToLast
} from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import { pushDataLayerForEvent } from '@lib/gtag';
import { Pulse } from '@atoms/Pulse';
import { useHotspot } from '@lib/hooks/useHotSpot';
import ScrollableFeed from 'react-scrollable-feed';
import { Input } from '@heroui/react';
import Image from 'next/image';
import { PaperPlaneTiltIcon as SendRoundedIcon } from '@phosphor-icons/react';

const ChatBox = ({ streamId, featureId, interactionType, showInputPulse }) => {
  const { isHotspotEnabled, handleHotspotClose } = useHotspot('sandbox:chat-input:hotspot');
  const [messages, setMessages] = useState([]);
  const userName = 'You';
  const activeUserId = 'user';
  const featureType = useRef(null);
  const [overlay, setOverlay] = useState(false);

  useEffect(() => {
    featureType.current = interactionType;
  }, [interactionType]);

  // eslint-disable-next-line camelcase
  const onComment = async ({ chat_input }) => {
    const rawMessagesRef = collection(db, 'streams', streamId, 'rawMessages');

    const document = {
      upvotes: 0,
      // eslint-disable-next-line camelcase
      message: chat_input,
      time: Date.now(),
      userId: activeUserId,
      name: userName,
      interaction: interactionType
    };

    setMessages((prev) => [...prev, document]);

    await addDoc(rawMessagesRef, document);
  };

  useEffect(() => {
    if (!streamId) return undefined;

    const unSubMessages = onSnapshot(
      query(
        collection(db, 'streams', streamId, 'processedMessages'),
        orderBy('time'),
        limitToLast(20)
      ),
      (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const document = {
              ...change.doc.data(),
              id: change.doc.id
            };

            if (
              document?.name === userName
              && document?.userId === activeUserId
            ) { return; }

            if (featureType.current !== document?.interaction) return;

            setMessages((prev) => [...prev, document]);
          }
        });
      }
    );

    return () => {
      unSubMessages();
    };
  }, [streamId]);

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    watch
  } = useForm({
    mode: 'onChange',
    resolver: yupValidator(
      yup.object().shape({
        chat_input: yup.string().required()
      })
    )
  });

  useEffect(() => {
    setFocus('chat_input');
  }, []);

  const handleComment = (data) => {
    pushDataLayerForEvent('sandbox_zoom_chatbox_send_message', { streamId }, false);
    onComment(data);
    reset({ chat_input: '' });
    setFocus('chat_input');
  };

  useEffect(() => {
    setMessages([]);
  }, [featureId]);

  const input = watch('chat_input');

  return (
    <div className='w-full flex flex-col p-2 bg-white border-b border-grey-200 text-grey-900'>
      <div className='flex py-2 px-2 gap-2 rounded-sm bg-[#F1F2F5]'>
        <Image
          className='rounded-full max-h-[35px] max-w-[35px]'
          alt='streamyard-logo'
          src='/images/sandbox/streamyard/logo.png'
          width={35}
          height={35}

        />
        <div>
          <p className="text-sm font-bold">StreamYard</p>
          <p className="text-sm font-normal text-[#1B1F29]">Live viewer comments show up on StreamYard.
            This is an example. Click on a comment to
            show it on screen.
          </p>
        </div>
      </div>

      <ScrollableFeed className='flex-1 scrollbar-hide'>
        {messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}
      </ScrollableFeed>

      <div className='p-2'>
        <form
          onSubmit={handleSubmit(handleComment)}
          className='p-2 rounded-ee-lg'>
          <div className='relative flex justify-center items-center gap-2'>
            <Image
              alt='streamyard-logo'
              src='/images/sandbox/streamyard/user.png'
              width={42}
              height={42}
            />

            <Input
              autoFocus
              size='lg'
              value={input}
              autoComplete='off'
              disableAnimation
              radius='sm'
              endContent={(
                <button type='submit'>
                  <SendRoundedIcon size={20} />
                </button>
              )}
              {...register('chat_input')}
              style={{ color: '#737487', fontSize: '10px' }}
              className="w-full font-normal text-neutral-60 md:leading-5"
              placeholder='Post a comment'
              variant='bordered'
            />
            {showInputPulse && isHotspotEnabled && (
              <div className='absolute -top-3 left-28'>
                <Pulse
                  isEnabled={isHotspotEnabled}
                  onClose={handleHotspotClose}
                  tooltip='Please type your response here; it will reflect in the interaction.'
                />
              </div>
            )}
          </div>

          <input type="submit" hidden />

        </form>
      </div>
    </div>
  );
};

const Message = ({ message }) => (
  <div className='flex gap-2 bg-[#F1F2F5] px-3 py-2 my-2 rounded-sm'>
    <Image
      className='rounded-full max-h-[40px] max-w-[40px] '
      alt='user'
      src='/images/sandbox/streamyard/user.png'
      width={40}
      height={40}
    />

    <div className='w-full'>
      <div className='flex flex-col font-medium w-full' >
        <div className='flex gap-2 items-center'>
          <p className='text-black/90 text-sm font-bold'>{message.name}</p>
          <p className='text-black/60 text-xs font-normal'>{format(new Date(message.time), 'h:mm a')}</p>
        </div>
        <p
          style={{ wordWrap: 'anywhere' }}
          className='font-normal text-sm'
        >
          {message.message}
        </p>
      </div>

    </div>
  </div>
);

export default ChatBox;
